import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PasskeysSection } from '../components/settings/PasskeysSection';
import { BiometricLockSection } from '../components/settings/BiometricLockSection';
import { LogoWave } from '../components/Logo';
import {
  User,
  Mail,
  Phone,
  Moon,
  Sun,
  Monitor,
  LogOut,
  ChevronRight,
  Bell,
  Shield,
  HelpCircle, X,
  Check,
  Camera,
  Lock,
  Trash2,
  Info,
  Download
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { exportAPI, usersAPI } from '../services/api';

type ThemeOption = 'light' | 'dark' | 'system';
type ModalType = 'theme' | 'editProfile' | 'notifications' | 'privacy' | 'help' | null;

export const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const { plan, isTrial, trialDaysRemaining, canUse, subscriptionsEnabled } = useSubscription();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Form states for edit profile
  const [editName, setEditName] = useState(user?.name || '');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [editPhone, setEditPhone] = useState(user?.phone || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Get user photo URL - prefer profile_photo, then avatar_url, then null
  const getUserPhotoUrl = () => {
    if (user?.profile_photo) {
      // profile_photo is a relative path like /uploads/profile-photos/...
      // nginx serves /uploads directly from backend folder
      return user.profile_photo;
    }
    if (user?.avatar_url) {
      return user.avatar_url;
    }
    return null;
  };

  const userPhotoUrl = getUserPhotoUrl();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const response = await usersAPI.updateProfile({
        name: editName,
        phone: editPhone,
      });
      if (response.success && response.data?.user) {
        // Update user in localStorage and context
        const updatedUser = { ...user, ...response.data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.location.reload(); // Refresh to get updated user data
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
      setActiveModal(null);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await exportAPI.exportData();
      if (response.success && response.data) {
        // Create and download JSON file
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `maldolor-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getThemeIcon = () => {
    if (theme === 'system') return <Monitor size={20} />;
    if (theme === 'dark') return <Moon size={20} />;
    return <Sun size={20} />;
  };

  const getThemeLabel = () => {
    if (theme === 'system') return 'Sistema';
    if (theme === 'dark') return 'Oscuro';
    return 'Claro';
  };

  const themeOptions: { value: ThemeOption; label: string; icon: React.ElementType }[] = [
    { value: 'light', label: 'Claro', icon: Sun },
    { value: 'dark', label: 'Oscuro', icon: Moon },
    { value: 'system', label: 'Sistema', icon: Monitor },
  ];

  const menuItems = [
    {
      icon: Bell,
      label: 'Notificaciones',
      sublabel: 'Recordatorios y alertas',
      modal: 'notifications' as ModalType,
    },
    {
      icon: Shield,
      label: 'Privacidad',
      sublabel: 'Datos y seguridad',
      modal: 'privacy' as ModalType,
    },
    {
      icon: HelpCircle,
      label: 'Ayuda',
      sublabel: 'Preguntas frecuentes',
      modal: 'help' as ModalType,
    },
  ];

  const closeModal = () => setActiveModal(null);

  // Reusable modal wrapper
  const ModalWrapper = ({ children, title }: { children: React.ReactNode; title: string }) => (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={closeModal}
      />

      {/* Mobile: Bottom sheet */}
      <motion.div
        initial={{ opacity: 0, y: '100%' }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[101] p-6 pb-safe rounded-t-3xl max-h-[85vh] overflow-y-auto"
        style={{ background: 'var(--color-surface)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
            {title}
          </h3>
          <button
            onClick={closeModal}
            className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
          >
            <X size={24} className="text-[var(--color-text-muted)]" />
          </button>
        </div>
        {children}
      </motion.div>

      {/* Desktop: Centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[101] items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md bg-[var(--color-surface)] rounded-3xl shadow-xl p-6 pointer-events-auto max-h-[85vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
              {title}
            </h3>
            <button
              onClick={closeModal}
              className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
            >
              <X size={24} className="text-[var(--color-text-muted)]" />
            </button>
          </div>
          {children}
        </motion.div>
      </div>
    </>
  );

  return (
    <div>
      <Header title="Perfil" />

      <main className="px-4 sm:px-6 pb-24 lg:pb-6 max-w-2xl lg:max-w-4xl mx-auto">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6 mb-6"
        >
          <div className="flex items-center gap-4">
            {userPhotoUrl ? (
              <img
                src={userPhotoUrl}
                alt={user?.name || 'Usuario'}
                className="w-20 h-20 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-display font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #E8927C 0%, #D4715E 100%)' }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-display font-semibold text-[var(--color-text)]">
                {user?.name || 'Usuario'}
              </h2>
              <p className="text-sm text-[var(--color-text-muted)] flex items-center gap-1 mt-1">
                <Mail size={14} />
                {user?.email || 'email@example.com'}
              </p>
              {user?.phone && (
                <p className="text-sm text-[var(--color-text-muted)] flex items-center gap-1 mt-1">
                  <Phone size={14} />
                  {user.phone}
                </p>
              )}
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setEditName(user?.name || '');
              setEditEmail(user?.email || '');
              setEditPhone(user?.phone || '');
              setActiveModal('editProfile');
            }}
            className="w-full mt-4 py-3 px-4 rounded-xl border-2 border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] font-medium flex items-center justify-center gap-2 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
          >
            <User size={18} />
            Editar perfil
          </motion.button>
        </motion.div>

        {/* Desktop two-column grid */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-6">
        <div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 mb-6"
        >
          <div className="card p-4 text-center">
            <p className="text-2xl font-display font-bold text-[var(--color-accent)]">
              {user?.stats?.totalHeadaches || 0}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">Dolores</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-display font-bold text-[#9CAF88]">
              {user?.stats?.totalTensions || 0}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">Tensiones</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-display font-bold text-[#7DD3FC]">
              {user?.stats?.streak || 0}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">Días racha</p>
          </div>
        </motion.div>

        {/* Subscription Plan Card - oculta mientras no haya planes de pago */}
        {subscriptionsEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-6"
        >
          <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 px-1">
            Plan
          </h3>
          <div className="card p-4">
            <div className="flex items-center gap-3 mb-2">
              <span style={{ fontSize: '24px' }}>&#x1F451;</span>
              <div>
                <p className="font-semibold text-[var(--color-text)]">
                  {plan === 'premium' ? 'Premium' : plan === 'trial' ? 'Trial Premium' : 'Gratuito'}
                </p>
                {isTrial && (
                  <p className="text-sm" style={{ color: trialDaysRemaining <= 2 ? '#ef4444' : 'var(--color-text-muted)' }}>
                    {trialDaysRemaining} dia{trialDaysRemaining !== 1 ? 's' : ''} restante{trialDaysRemaining !== 1 ? 's' : ''}
                  </p>
                )}
                {plan === 'free' && (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    Contacta al admin para activar Premium
                  </p>
                )}
                {plan === 'premium' && (
                  <p className="text-sm text-green-500">Acceso completo</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Theme Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 px-1">
            Apariencia
          </h3>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveModal('theme')}
            className="card w-full p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: isDark ? '#FBBF2420' : '#64748B20' }}
            >
              {getThemeIcon()}
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-[var(--color-text)]">Tema</p>
              <p className="text-sm text-[var(--color-text-muted)]">{getThemeLabel()}</p>
            </div>
            <ChevronRight size={20} className="text-[var(--color-text-muted)]" />
          </motion.button>
        </motion.div>

        </div>{/* end left column */}
        <div>

        {/* Settings Menu */}
        <PasskeysSection />

        <BiometricLockSection />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 px-1">
            Configuración
          </h3>
          <div className="card overflow-hidden divide-y divide-[var(--color-border)]">
            {menuItems.map((item) => (
              <motion.button
                key={item.label}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveModal(item.modal)}
                className="w-full p-4 flex items-center gap-4 hover:bg-[var(--color-bg)] transition-colors"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-bg)]">
                  <item.icon size={20} className="text-[var(--color-text-muted)]" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-[var(--color-text)]">{item.label}</p>
                  <p className="text-sm text-[var(--color-text-muted)]">{item.sublabel}</p>
                </div>
                <ChevronRight size={20} className="text-[var(--color-text-muted)]" />
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Logout Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="w-full p-4 rounded-2xl border-2 border-red-200 dark:border-red-800 text-red-500 font-medium flex items-center justify-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          {isLoggingOut ? (
            <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <LogOut size={20} />
              Cerrar sesión
            </>
          )}
        </motion.button>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center justify-center gap-2 mb-2">
            <LogoWave size={18} style={{ color: '#E8927C' }} />
            <span className="text-sm font-medium text-[var(--color-text)]">MalDolor</span>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">Versión 2.0.0</p>
        </motion.div>

        </div>{/* end right column */}
        </div>{/* end desktop two-column grid */}
      </main>

      {/* Modals */}
      <AnimatePresence>
        {/* Theme Modal */}
        {activeModal === 'theme' && (
          <ModalWrapper title="Seleccionar tema">
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map((option) => (
                <motion.button
                  key={option.value}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setTheme(option.value);
                    setActiveModal(null);
                  }}
                  className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 ${
                    theme === option.value
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                      : 'border-transparent bg-[var(--color-bg)]'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    theme === option.value ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-surface)]'
                  }`}>
                    <option.icon
                      size={24}
                      className={theme === option.value ? 'text-white' : 'text-[var(--color-text-muted)]'}
                    />
                  </div>
                  <span className={`text-sm font-medium ${
                    theme === option.value ? 'text-[var(--color-accent)]' : 'text-[var(--color-text)]'
                  }`}>
                    {option.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </ModalWrapper>
        )}

        {/* Edit Profile Modal */}
        {activeModal === 'editProfile' && (
          <ModalWrapper title="Editar perfil">
            <div className="space-y-4">
              {/* Avatar */}
              <div className="flex justify-center">
                <div className="relative">
                  {userPhotoUrl ? (
                    <img
                      src={userPhotoUrl}
                      alt={editName || 'Usuario'}
                      className="w-24 h-24 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl flex items-center justify-center text-4xl font-display font-bold text-white"
                      style={{ background: 'linear-gradient(135deg, #E8927C 0%, #D4715E 100%)' }}
                    >
                      {editName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <label className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[var(--color-accent)] text-white flex items-center justify-center shadow-lg cursor-pointer hover:bg-[var(--color-accent-dark)] transition-colors">
                    <Camera size={16} />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const response = await usersAPI.uploadPhoto(file);
                            if (response.success) {
                              window.location.reload();
                            }
                          } catch (error) {
                            console.error('Error uploading photo:', error);
                          }
                        }
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                  Nombre
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input"
                  placeholder="Tu nombre"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  className="input"
                  placeholder="Tu teléfono"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editEmail}
                  readOnly
                  className="input opacity-60 cursor-not-allowed"
                  placeholder="tu@email.com"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  El email no se puede modificar
                </p>
              </div>

              {/* Change Password */}
              <button className="w-full p-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text)] flex items-center justify-center gap-2 hover:bg-[var(--color-bg)] transition-colors">
                <Lock size={18} />
                Cambiar contraseña
              </button>

              {/* Save Button */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="btn btn-primary w-full py-4"
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Check size={20} />
                    Guardar cambios
                  </>
                )}
              </motion.button>
            </div>
          </ModalWrapper>
        )}

        {/* Notifications Modal */}
        {activeModal === 'notifications' && (
          <ModalWrapper title="Notificaciones">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--color-bg)]">
                <div>
                  <p className="font-medium text-[var(--color-text)]">Recordatorios diarios</p>
                  <p className="text-sm text-[var(--color-text-muted)]">Registrar síntomas</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-accent)]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--color-bg)]">
                <div>
                  <p className="font-medium text-[var(--color-text)]">Recordatorio medicación</p>
                  <p className="text-sm text-[var(--color-text-muted)]">Tomar medicamentos</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-accent)]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--color-bg)]">
                <div>
                  <p className="font-medium text-[var(--color-text)]">Resumen semanal</p>
                  <p className="text-sm text-[var(--color-text-muted)]">Email con estadísticas</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-accent)]"></div>
                </label>
              </div>

              <p className="text-xs text-[var(--color-text-muted)] text-center mt-4">
                Las notificaciones push requieren permisos del navegador
              </p>
            </div>
          </ModalWrapper>
        )}

        {/* Privacy Modal */}
        {activeModal === 'privacy' && (
          <ModalWrapper title="Privacidad y seguridad">
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <div className="flex items-start gap-3">
                  <Shield size={24} className="text-[var(--color-accent)] mt-0.5" />
                  <div>
                    <p className="font-medium text-[var(--color-text)]">Tus datos están seguros</p>
                    <p className="text-sm text-[var(--color-text-muted)] mt-1">
                      Todos tus datos de salud están encriptados y solo tú tienes acceso a ellos.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={canUse('export_data') ? handleExportData : undefined}
                disabled={isExporting || !canUse('export_data')}
                className="w-full p-4 rounded-xl border border-[var(--color-border)] text-[var(--color-text)] flex items-center gap-3 hover:bg-[var(--color-bg)] transition-colors disabled:opacity-50"
              >
                {isExporting ? (
                  <div className="w-5 h-5 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download size={20} className="text-[var(--color-accent)]" />
                )}
                <div className="flex-1 text-left">
                  <p className="font-medium">Exportar mis datos</p>
                  <p className="text-sm text-[var(--color-text-muted)]">
                    {canUse('export_data') ? 'Descargar en formato JSON' : 'Requiere Premium'}
                  </p>
                </div>
                {!canUse('export_data') ? (
                  <Lock size={16} className="text-[var(--color-text-muted)]" />
                ) : (
                  <ChevronRight size={20} className="text-[var(--color-text-muted)]" />
                )}
              </button>

              <button className="w-full p-4 rounded-xl border border-red-200 dark:border-red-800 text-red-500 flex items-center gap-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <Trash2 size={20} />
                <div className="flex-1 text-left">
                  <p className="font-medium">Eliminar cuenta</p>
                  <p className="text-sm opacity-70">Esta acción es irreversible</p>
                </div>
                <ChevronRight size={20} />
              </button>
            </div>
          </ModalWrapper>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <ModalWrapper title="Ayuda">
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="font-medium text-[var(--color-text)] mb-2">¿Cómo registro un dolor de cabeza?</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Desde la pantalla principal, pulsa el botón "Dolor" o selecciona un día en el calendario para añadir un registro.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="font-medium text-[var(--color-text)] mb-2">¿Cómo veo mis estadísticas?</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Ve a la pestaña "Estadísticas" en la barra de navegación para ver gráficos y tendencias de tus registros.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="font-medium text-[var(--color-text)] mb-2">¿Puedo editar un registro?</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Sí, pulsa sobre cualquier registro en el calendario o en la lista para ver los detalles y editarlo.
                </p>
              </div>

              <div className="mt-6 p-4 rounded-xl border border-[var(--color-border)] text-center">
                <p className="text-sm text-[var(--color-text-muted)] mb-2">¿Necesitas más ayuda?</p>
                <a
                  href="mailto:soporte@maldolor.com"
                  className="text-[var(--color-accent)] font-medium"
                >
                  soporte@maldolor.com
                </a>
              </div>
            </div>
          </ModalWrapper>
        )}
      </AnimatePresence>
    </div>
  );
};
